import React, { useEffect } from 'react';

const InterfaceSegregationPrinciple = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Interface Segregation Principle (ISP) - SOLID Principles Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Interface Segregation Principle - no client should be forced to depend on methods it does not use. Learn to create focused interfaces.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Interface Segregation Principle - no client should be forced to depend on methods it does not use. Learn to create focused interfaces.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'interface segregation principle, ISP, SOLID principles, interface design, software architecture, clean code');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'interface segregation principle, ISP, SOLID principles, interface design, software architecture, clean code';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Interface Segregation Principle (ISP)</h1>
        <p style={styles.subtitle}>
          The Fourth SOLID Principle - Create focused, client-specific interfaces
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Definition</h2>
          <blockquote style={styles.blockquote}>
            "No client should be forced to depend on methods it does not use."
          </blockquote>
          <p style={styles.paragraph}>
            The Interface Segregation Principle states that clients should not be forced to depend on interfaces 
            they don't use. Instead of one large interface, create multiple smaller, more specific interfaces 
            so that clients only need to know about the methods that are of interest to them.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Key Concepts</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Focused Interfaces:</strong> Interfaces should be specific to client needs</li>
            <li style={styles.listItem}><strong>Role-based Design:</strong> Design interfaces around roles, not capabilities</li>
            <li style={styles.listItem}><strong>Minimal Dependencies:</strong> Clients depend only on what they actually need</li>
            <li style={styles.listItem}><strong>High Cohesion:</strong> Related methods should be grouped together</li>
            <li style={styles.listItem}><strong>Composition:</strong> Combine multiple small interfaces when needed</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Violation Example</h2>
          <div style={styles.codeBlock}>
{`// BAD: Violates ISP - Fat interface
interface Worker {
    void work();
    void eat();
    void sleep();
    void code();
    void test();
    void deploy();
    void managePeople();
    void attendMeeting();
}

// Problem: All classes must implement all methods
class Developer implements Worker {
    @Override
    public void work() { System.out.println("Writing code"); }
    
    @Override
    public void eat() { System.out.println("Eating lunch"); }
    
    @Override
    public void sleep() { System.out.println("Sleeping"); }
    
    @Override
    public void code() { System.out.println("Coding"); }
    
    @Override
    public void test() { System.out.println("Testing code"); }
    
    @Override
    public void deploy() { System.out.println("Deploying"); }
    
    // Developer forced to implement management methods
    @Override
    public void managePeople() {
        throw new UnsupportedOperationException("Developer doesn't manage people");
    }
    
    @Override
    public void attendMeeting() {
        throw new UnsupportedOperationException("Developer avoids meetings");
    }
}

class Manager implements Worker {
    @Override
    public void work() { System.out.println("Managing work"); }
    
    @Override
    public void eat() { System.out.println("Eating lunch"); }
    
    @Override
    public void sleep() { System.out.println("Sleeping"); }
    
    @Override
    public void managePeople() { System.out.println("Managing team"); }
    
    @Override
    public void attendMeeting() { System.out.println("Attending meetings"); }
    
    // Manager forced to implement technical methods
    @Override
    public void code() {
        throw new UnsupportedOperationException("Manager doesn't code");
    }
    
    @Override
    public void test() {
        throw new UnsupportedOperationException("Manager doesn't test");
    }
    
    @Override
    public void deploy() {
        throw new UnsupportedOperationException("Manager doesn't deploy");
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Correct Implementation</h2>
          <div style={styles.codeBlock}>
{`// GOOD: Following ISP - Segregated interfaces
interface Workable {
    void work();
}

interface Eatable {
    void eat();
}

interface Sleepable {
    void sleep();
}

interface Codeable {
    void code();
    void test();
    void deploy();
}

interface Manageable {
    void managePeople();
    void attendMeeting();
}

// Classes implement only what they need
class Developer implements Workable, Eatable, Sleepable, Codeable {
    @Override
    public void work() {
        System.out.println("Writing code");
    }
    
    @Override
    public void eat() {
        System.out.println("Eating lunch");
    }
    
    @Override
    public void sleep() {
        System.out.println("Sleeping");
    }
    
    @Override
    public void code() {
        System.out.println("Coding");
    }
    
    @Override
    public void test() {
        System.out.println("Testing code");
    }
    
    @Override
    public void deploy() {
        System.out.println("Deploying");
    }
}

class Manager implements Workable, Eatable, Sleepable, Manageable {
    @Override
    public void work() {
        System.out.println("Managing work");
    }
    
    @Override
    public void eat() {
        System.out.println("Eating lunch");
    }
    
    @Override
    public void sleep() {
        System.out.println("Sleeping");
    }
    
    @Override
    public void managePeople() {
        System.out.println("Managing team");
    }
    
    @Override
    public void attendMeeting() {
        System.out.println("Attending meetings");
    }
}

// Technical Manager can implement both
class TechnicalManager implements Workable, Eatable, Sleepable, Codeable, Manageable {
    // Implements all methods from both interfaces
    // ... implementation details
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Benefits of Following ISP</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Reduced Coupling:</strong> Clients depend only on what they need</li>
            <li style={styles.listItem}><strong>Easier Testing:</strong> Smaller interfaces are easier to mock and test</li>
            <li style={styles.listItem}><strong>Better Maintainability:</strong> Changes to one interface don't affect unrelated clients</li>
            <li style={styles.listItem}><strong>Clearer Design:</strong> Interfaces express specific roles and responsibilities</li>
            <li style={styles.listItem}><strong>Flexibility:</strong> Classes can implement multiple focused interfaces</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use ISP</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you have large interfaces with many methods</li>
            <li style={styles.listItem}>When clients only use a subset of interface methods</li>
            <li style={styles.listItem}>When you want to reduce coupling between components</li>
            <li style={styles.listItem}>When different clients need different sets of functionality</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Interface Segregation Principle promotes the creation of focused, client-specific interfaces. 
            By avoiding fat interfaces and creating smaller, more cohesive ones, you reduce coupling, 
            improve maintainability, and make your code more flexible and testable.
          </p>
          <p style={styles.paragraph}>
            Remember: it's better to have many small, focused interfaces than one large, general-purpose interface. 
            This approach leads to cleaner, more maintainable code that's easier to understand and modify.
          </p>
        </section>
      </main>
    </article>
  );
};

export default InterfaceSegregationPrinciple;